import { getFieldMetadata } from '@shared/components/ScheduleEditor/hooks/getFieldMetadata';
import { FieldMetadata } from '@shared/components/ScheduleEditor/hooks/useScheduleFormState';
import type { ScheduleEditorData } from '@shared/components/ScheduleEditor/ScheduleEditor.types';
import { useTranslation } from '@shared/core';
import { Flex, FormControl, InputV2, InputV2Props, styled, TextV2, useToast } from '@withjoy/joykit';
import React, { useCallback } from 'react';

const StyledTextArea = styled.textarea`
  width: 100%;
  resize: none;
  height: 100px;
  font-size: 14px;
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #ccc;
  font-family: inherit;
`;

type FormTextFieldProps = {
  fieldName: keyof ScheduleEditorData;
  state: ScheduleEditorData;
  setValue: (key: keyof ScheduleEditorData, value: string) => void;
  label?: string;
  meta: Record<keyof ScheduleEditorData, FieldMetadata>;
  textField?: boolean;
  maxLength?: number | null;
} & Omit<InputV2Props, 'value' | 'onChange'>;

export const FormTextField = (props: FormTextFieldProps) => {
  const { tq } = useTranslation('scheduleEditor');
  const { state, setValue, fieldName, textField, label, meta } = props;
  const metadata = getFieldMetadata(fieldName, meta);
  const { toast } = useToast();

  const value = (state[fieldName] as string) ?? '';
  const maxLength = props.maxLength === undefined ? 255 : props.maxLength;
  const length = value.length;

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const truncatedValue = event.target.value.slice(0, maxLength !== null ? maxLength : undefined);

      setValue(fieldName, truncatedValue);

      const isTruncated = truncatedValue !== event.target.value;
      const isPaste = event.nativeEvent instanceof InputEvent && event.nativeEvent.inputType === 'insertFromPaste';

      if (isPaste && isTruncated) {
        toast(tq`You have reached the maximum length for this field.`);
      }
    },
    [toast, tq, fieldName, setValue, maxLength],
  );

  return (
    <FormControl
      label={label && <TextV2 typographyVariant="label3">{label}</TextV2>}
      isInvalid={metadata.error}
    >
      <Flex
        flexDirection="column"
        gap="8px"
      >
        {textField ? (
          <StyledTextArea
            value={value}
            onChange={handleChange}
          />
        ) : (
          <InputV2
            value={value}
            onChange={handleChange}
          />
        )}
        {maxLength && (
          <TextV2
            textAlign="right"
            typographyVariant="label2"
          >
            {`${length} / ${maxLength}`}
          </TextV2>
        )}
      </Flex>
    </FormControl>
  );
};
